import cx from 'classnames'
import { useFeature } from '@growthbook/growthbook-react'
import { Link, useMatch } from 'features/i18n/router'
import { isNil } from 'lodash'
import { useCallback, useRef, useState } from 'react'
import { useTranslation } from 'react-i18next'
import { useDispatch } from 'react-redux'

import useChatRoomLogic from 'features/Chat/hocs/useChatRoomLogic'
import { useDetectClickOutside } from 'hooks'
import { ROUTE_PATH } from 'lib/paths'
import { Group, Post } from 'lib/_types'
import { togglePostBoxModal } from 'store/actions'
import { toggleMovePostToThreadConfirmBox } from 'store/actions/confirmBox'
import { useCurrentUser } from 'store/api/yay/user'
import { useReportModal } from 'components/Modal/ReportModal/ReportModal'

const PostMenu = ({
  post,
  index,
  isGroupPost,
  onDeletePost,
  onBlockUser,
  onPinPost,
  onUnpinPost,
  onHighlight,
  onUnHighlight,
  onBookmark,
  onUnBookmark,
  onBanUser,
  onMuteUser,
  group,
  isMuted,
}: {
  post: Post
  index?: number
  isGroupPost: boolean
  onDeletePost: Function
  onBlockUser: Function
  onPinPost: Function
  onUnpinPost: Function
  onHighlight: Function
  onUnHighlight: Function
  onBookmark: Function
  onUnBookmark: Function
  onBanUser: Function
  onMuteUser: Function
  group?: {
    data?: Group
  }
  isMuted?: boolean
}) => {
  const dispatch = useDispatch()
  const { t } = useTranslation()
  const isEditPostEnabled = useFeature('yay-12028')
  const isMovePostToThreadEnabled = useFeature('yay-11880').on
  const reportModal = useReportModal()

  const [isActive, setIsActive] = useState(false)
  const menuRef = useRef(null)

  const onToggleMenu = useCallback(() => {
    setIsActive(!isActive)
  }, [isActive])

  useDetectClickOutside(menuRef, () => setIsActive(false), isActive)

  const { onCreateChatRoom } = useChatRoomLogic()

  const currentUserId = useCurrentUser()?.data?.id

  const isUserDetailPage = Boolean(useMatch(ROUTE_PATH.USER()))
  const isHighlightPage = Boolean(useMatch(ROUTE_PATH.GROUP_HIGHLIGHTS()))
  const isBookmarkPage = Boolean(useMatch(ROUTE_PATH.BOOKMARKS))

  const isGroupOwner = currentUserId == group?.data?.owner?.id
  const isCurrentUserPost = currentUserId == post?.user?.id
  const isPinnedPost = post?._pinned

  const groupUserRole = isGroupOwner
    ? 'owner'
    : group?.data?.moderator_ids?.includes?.(currentUserId)
    ? 'moderator'
    : group?.data?.is_joined
    ? 'member'
    : 'other'

  const hasModRights = ['owner', 'moderator'].includes(groupUserRole)

  const bannable =
    !isCurrentUserPost &&
    isGroupPost &&
    hasModRights &&
    post?.user?.id != group?.data?.owner?.id &&
    !post?.user?.group_user?.is_moderator

  const mutable =
    !isCurrentUserPost &&
    isGroupPost &&
    post?.user?.id != group?.data?.owner?.id &&
    ((groupUserRole === 'moderator' && !post?.user?.group_user?.is_moderator) ||
      groupUserRole === 'owner')

  const pinnable =
    !isHighlightPage &&
    isNil(post?.thread_id) &&
    ((isUserDetailPage && isCurrentUserPost) || (isGroupPost && isGroupOwner))

  const highlightable = isGroupPost && hasModRights && post?.post_type != 'call'

  const isHighlighted = post?.highlighted

  const bookmarkable = post?.post_type != 'call'

  const options = [
    {
      condition: isCurrentUserPost || (isGroupPost && hasModRights),
      label: t('common.delete'),
      onClick: () => onDeletePost(post),
      style: 'warning',
    },
    {
      condition: isCurrentUserPost && isEditPostEnabled?.on,
      label: t('common.edit'),
      onClick: () => {
        dispatch(
          togglePostBoxModal({
            post,
            mode: 'edit',
          })
        )
      },
    },
    {
      condition: !isCurrentUserPost,
      label: t('common.block'),
      onClick: () => onBlockUser(post, index),
      style: 'warning',
    },
    {
      condition: bannable,
      label: t('common.ban'),
      onClick: () => onBanUser(post),
      style: 'warning',
    },
    {
      condition: mutable,
      label: t('common.mute'),
      onClick: () => onMuteUser(post),
      style: 'warning',
    },
    {
      condition: !isCurrentUserPost,
      label: t('common.report'),
      style: 'warning',
      onClick: () =>
        reportModal.open({
          postId: post?.id?.toString?.(),
          postType: post?.post_type,
          postLocation: post?.group_id ? 'group' : 'timeline',
          postIndex: index,
          userId: post?.user?.id?.toString?.(),
        }),
    },
    {
      condition: !isCurrentUserPost,
      label: t('common.chat'),
      onClick: () =>
        onCreateChatRoom({
          userId: post?.user?.id,
          from: 'post',
        }),
    },
    {
      condition: pinnable && !isPinnedPost && !isMuted,
      label: t('common.pin'),
      onClick: () => onPinPost(post),
    },
    {
      condition: pinnable && isPinnedPost,
      label: t('common.unpin'),
      onClick: () => onUnpinPost(post),
    },
    {
      condition:
        isMovePostToThreadEnabled &&
        post?.post_type != 'call' &&
        post?.post_type != 'repost' &&
        post?.post_type != 'thread' &&
        post?.post_type != 'shareable_thread' &&
        isNil(post?.in_reply_to) &&
        isNil(post?.thread_id) &&
        (['owner', 'moderator'].includes(groupUserRole) ||
          (isCurrentUserPost &&
            group?.data?.allow_thread_creation_by == 'member')) &&
        !isMuted,
      label: t('post.actions.convert_to_thread'),
      onClick: () => {
        dispatch(
          toggleMovePostToThreadConfirmBox({
            post,
            isAddToExistingThreadEnabled: group?.data?.threads_count > 0,
          })
        )
      },
    },
    {
      condition: highlightable && !isHighlighted,
      label: t('post.actions.highlight_add'),
      onClick: () => onHighlight(post),
    },
    {
      condition: highlightable && isHighlighted,
      label: t('post.actions.highlight_remove'),
      onClick: () => onUnHighlight(post),
    },
    {
      condition: bookmarkable && !isBookmarkPage && !isNil(onBookmark),
      label: t('post.actions.bookmark_add'),
      onClick: () => onBookmark(post),
    },
    {
      condition: bookmarkable && isBookmarkPage && !isNil(onUnBookmark),
      label: t('post.actions.bookmark_remove'),
      onClick: () => onUnBookmark(post),
    },
  ]

  return (
    <div
      className={cx('Menu', {
        'Menu--active': isActive,
      })}
      ref={menuRef}
    >
      <a className="Menu__handle" onClick={onToggleMenu} />

      <div className="Menu__panel Menu__panel--arrow-right">
        <ul className="Menu__panel__ul" onClick={onToggleMenu}>
          {options.map((option, i) => (
            <PostMenuOption key={`${option.label}-${i}`} {...option} />
          ))}
        </ul>
      </div>
    </div>
  )
}

export default PostMenu

const PostMenuOption = ({
  condition,
  label,
  onClick,
  to,
  style,
}: {
  condition: boolean
  label: string
  to?: string
  onClick?: Function
  style?: string
}) => {
  if (!condition) return null

  const Elm = (to ? Link : 'a') as any

  return (
    <li>
      <Elm
        to={to}
        onClick={onClick}
        className={cx('Menu__panel__ul__a', {
          'Menu__panel__ul__a--warning': style === 'warning',
        })}
      >
        {label}
      </Elm>
    </li>
  )
}
